import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

export default function CustomerProfile() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ phone: "", city: "", email: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    api.get("/customer/me").then((r) => {
      setC(r.data);
      setForm({ phone: r.data.phone || "", city: r.data.city || "", email: r.data.email || "" });
    });
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/customer/me", form);
      setC(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill in all password fields"); return; }
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/customer/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  if (!c) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <>
      <PageHeader title="My Profile" subtitle="Manage your contact details" />
      <div className="p-8 max-w-2xl space-y-6">
        <Card className="rounded-sm border-border p-6">
          <div className="mb-5">
            <p className="label-caps">Name</p>
            <p className="font-heading font-semibold text-lg mt-1">{c.name}</p>
          </div>
          <div className="space-y-4" data-testid="customer-profile-form">
            {[["email", "Email"], ["phone", "Phone"], ["city", "City"]].map(([k, l]) => (
              <div key={k}>
                <Label className="label-caps">{l}</Label>
                <Input data-testid={`cprofile-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm mt-1.5" />
              </div>
            ))}
            <Button className="rounded-sm" onClick={save} data-testid="cprofile-save">Save Changes</Button>
          </div>
        </Card>

        <Card className="rounded-sm border-border p-6" data-testid="customer-change-password">
          <h3 className="font-heading font-semibold text-lg">Change Password</h3>
          <p className="text-sm text-muted-foreground mt-1">If you signed in with a one-time password, set your own secure password here.</p>
          <div className="space-y-4 mt-5">
            <div>
              <Label className="label-caps">Current Password</Label>
              <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-sm mt-1.5" data-testid="cpw-current" autoComplete="current-password" />
            </div>
            <div>
              <Label className="label-caps">New Password</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-sm mt-1.5" data-testid="cpw-new" autoComplete="new-password" />
            </div>
            <div>
              <Label className="label-caps">Confirm New Password</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="rounded-sm mt-1.5" data-testid="cpw-confirm" autoComplete="new-password" />
            </div>
            <Button className="rounded-sm" onClick={changePassword} disabled={pwBusy} data-testid="cpw-save">{pwBusy ? "Updating…" : "Update Password"}</Button>
          </div>
        </Card>
      </div>
    </>
  );
}
